import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── helpers ─────────────────────────────────────────── */
function formatTime(date) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}
function formatDate(date) {
  return date.toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

/* ─── E-Ink friendly global styles injected once ───────── */
const EINK_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #fff; }
  .eink-root {
    font-family: 'Space Grotesk', sans-serif;
    background: #ffffff;
    color: #000000;
    min-height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    print-color-adjust: exact;
  }
  .eink-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 36px 48px 24px;
    border-bottom: 2px solid #000;
  }
  .eink-room-name { font-size: 52px; font-weight: 800; line-height: 1.1; letter-spacing: -1px; }
  .eink-room-type { font-size: 18px; font-weight: 400; color: #444; text-transform: capitalize; margin-top: 6px; }
  .eink-clock { text-align: right; }
  .eink-time { font-size: 64px; font-weight: 800; letter-spacing: -2px; font-variant-numeric: tabular-nums; }
  .eink-date { font-size: 16px; color: #444; margin-top: 4px; }
  .eink-body {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 48px;
    gap: 64px;
  }
  .eink-status-block { flex: 1; }
  .eink-status-label {
    display: inline-block;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 4px;
    text-transform: uppercase;
    border: 2px solid #000;
    padding: 6px 16px;
    margin-bottom: 24px;
  }
  .eink-status-label.available { background: #000; color: #fff; }
  .eink-status-label.occupied  { border-color: #000; }
  .eink-big-text {
    font-size: 96px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -3px;
    text-transform: uppercase;
  }
  .eink-booked-for {
    font-size: 36px;
    font-weight: 600;
    color: #333;
    margin-top: 12px;
    letter-spacing: -0.5px;
  }
  .eink-booked-by {
    font-size: 18px;
    color: #666;
    margin-top: 6px;
  }
  .eink-sub-text { font-size: 32px; font-weight: 400; margin-top: 16px; color: #222; font-variant-numeric: tabular-nums; }
  .eink-meeting-name { font-size: 28px; font-weight: 600; color: #444; margin-bottom: 8px; letter-spacing: -0.5px; }
  .eink-until  { font-size: 20px; color: #555; margin-top: 10px; }
  .eink-next-booking-block {
    margin-top: 28px;
    padding-top: 24px;
    border-top: 1px solid #ccc;
  }
  .eink-next-label {
    font-size: 11px; font-weight: 700; letter-spacing: 4px;
    text-transform: uppercase; color: #888; margin-bottom: 8px;
  }
  .eink-next-meeting {
    font-size: 32px; font-weight: 800; letter-spacing: -1px; line-height: 1.1;
  }
  .eink-next-detail {
    font-size: 18px; color: #444; margin-top: 4px;
  }
  .eink-qr-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  .eink-qr-label { font-size: 13px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; text-align: center; }
  .eink-footer {
    border-top: 2px solid #000;
    padding: 20px 48px;
  }
  .eink-footer-label { font-size: 11px; font-weight: 700; letter-spacing: 4px; text-transform: uppercase; color: #555; margin-bottom: 12px; }
  .eink-next-list { display: flex; gap: 24px; }
  .eink-next-item {
    border: 1.5px solid #000;
    padding: 10px 18px;
    min-width: 180px;
  }
  .eink-next-time { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .eink-next-name { font-size: 13px; color: #444; margin-top: 2px; }
  .eink-divider { width: 100%; height: 2px; background: #000; }
  .eink-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    gap: 16px;
  }
  .eink-error h1 { font-size: 48px; font-weight: 800; }
  .eink-error p  { font-size: 20px; color: #555; }
`;

export default function RoomDisplay() {
  const { spaceId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [time, setTime] = useState(new Date());

  const bookingUrl = `${window.location.origin}/book/${spaceId}`;

  const serverOffsetRef = useRef(0);

  const fetchData = useCallback(() => {
    if (!token) { setError("No display token provided"); return; }
    axios
      .get(`${API}/display/${spaceId}?token=${token}`)
      .then((r) => {
        setData(r.data);
        setError(null);
        if (r.data.server_time) {
          const serverNow = new Date(r.data.server_time);
          serverOffsetRef.current = serverNow.getTime() - Date.now();
          setTime(serverNow);
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "Failed to load display data"));
  }, [spaceId, token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const tick = setInterval(() => setTime(new Date(Date.now() + serverOffsetRef.current)), 1000);
    return () => clearInterval(tick);
  }, []);

  /* Inject e-ink stylesheet once */
  useEffect(() => {
    const id = "eink-styles";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = EINK_STYLE;
      document.head.appendChild(style);
    }
    return () => {};
  }, []);

  /* ── error state ─────────────────────────────────────── */
  if (error) {
    return (
      <div className="eink-root" data-testid="display-error">
        <div className="eink-error">
          <h1>Display Error</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  /* ── loading state ───────────────────────────────────── */
  if (!data) {
    return (
      <div className="eink-root" data-testid="display-loading">
        <div className="eink-error">
          <h1>Loading…</h1>
          <p>Fetching room status</p>
        </div>
      </div>
    );
  }

  const isOccupied    = data.is_occupied;
  const current       = data.current_booking;
  const nextBookings  = data.next_bookings || [];

  return (
    <div className="eink-root" data-testid="room-display">

      {/* ── Header: logo + room name + clock ─────────── */}
      <div className="eink-header">
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <div style={{ fontWeight: 800, fontSize: "28px", letterSpacing: "-1px", lineHeight: 1, borderRight: "2px solid #000", paddingRight: "24px" }} data-testid="display-logo">
            THE RIG
          </div>
          <div>
            <div className="eink-room-name" data-testid="display-room-name">
              {data.space?.name}
            </div>
            <div className="eink-room-type">
              {data.space?.type?.replace(/_/g, " ")}
            </div>
          </div>
        </div>
        <div className="eink-clock">
          <div className="eink-time" data-testid="display-clock">
            {formatTime(time)}
          </div>
          <div className="eink-date">{formatDate(time)}</div>
        </div>
      </div>

      {/* ── Body: status + optional QR ───────────────── */}
      <div className="eink-body">

        {isOccupied ? (
          /* ── Occupied ────────────────────────────────── */
          <div className="eink-status-block" data-testid="display-occupied">
            <div className="eink-status-label occupied">In Use</div>

            {/* Meeting name in BIG letters */}
            <div className="eink-big-text" data-testid="display-meeting-name">
              {current?.meeting_name || current?.booked_for || current?.name || "Reserved"}
            </div>

            {/* Booked for / booked by — smaller text */}
            {current?.booked_for && (
              <div className="eink-booked-for" data-testid="display-booked-for">
                {current.booked_for}
              </div>
            )}
            {current?.booked_for && current?.name && (
              <div className="eink-booked-by" data-testid="display-booked-by">
                Booked by {current.name}
              </div>
            )}
            {!current?.booked_for && current?.meeting_name && current?.name && (
              <div className="eink-booked-for" data-testid="display-current-name">
                {current.name}
              </div>
            )}

            <div className="eink-sub-text" data-testid="display-current-time">
              {current?.start} — {current?.end}
            </div>

            {/* Next booking — shown separately */}
            {nextBookings.length > 0 && (
              <div className="eink-next-booking-block" data-testid="display-next-up">
                <div className="eink-next-label">Next</div>
                <div className="eink-next-meeting">
                  {nextBookings[0].meeting_name || nextBookings[0].booked_for || nextBookings[0].name}
                </div>
                <div className="eink-next-detail">
                  {nextBookings[0].start} — {nextBookings[0].end}
                  {nextBookings[0].booked_for && nextBookings[0].name ? ` · ${nextBookings[0].name}` : ""}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── Available ───────────────────────────────── */
          <>
            <div className="eink-status-block" data-testid="display-available">
              <div className="eink-status-label available">Available</div>
              <div className="eink-big-text">Available</div>
              {data.available_until && (
                <div className="eink-until" data-testid="display-available-until">
                  Until {data.available_until}
                </div>
              )}
            </div>

            {/* QR code — only when available */}
            <div className="eink-qr-block" data-testid="display-qr">
              <QRCodeSVG
                value={bookingUrl}
                size={200}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
                marginSize={1}
              />
              <div className="eink-qr-label">Scan to Book</div>
            </div>
          </>
        )}
      </div>

      {/* ── Footer: upcoming bookings ─────────────────── */}
      {nextBookings.length > 0 && (
        <div className="eink-footer" data-testid="display-next-bookings">
          <div className="eink-footer-label">Upcoming Today</div>
          <div className="eink-next-list">
            {nextBookings.slice(0, 4).map((nb, i) => (
              <div key={i} className="eink-next-item" data-testid={`display-next-${i}`}>
                <div className="eink-next-time">{nb.start} — {nb.end}</div>
                <div className="eink-next-name">
                  {nb.meeting_name ? `${nb.meeting_name} — ` : ""}
                  {nb.booked_for ? `${nb.booked_for} (by ${nb.name})` : nb.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
